'use client'

import { useMemo, useState } from 'react'
import {
  EntityListShell,
  PageHeader,
  Pagination,
  SearchInput,
  Spinner,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@rr/ui'
import { useDriversList } from '@/modules/drivers/hooks/useDrivers'

const PAGE_SIZE = 10

export default function DriversPage() {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const filters = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search: search.trim() || undefined,
    }),
    [page, search]
  )

  const { data, isLoading, isError, error } = useDriversList(filters as any)
  const items: any[] = (data as any)?.items ?? (data as any)?.data?.items ?? []
  const total: number =
    (data as any)?.pagination?.total ??
    (data as any)?.data?.pagination?.total ??
    items.length

  return (
    <div className="page-pad">
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader
          title="Drivers"
          subtitle="Drivers across every tenant fleet."
        />

        <EntityListShell
          toolbar={
            <SearchInput
              value={search}
              onChange={(v) => {
                setSearch(v)
                setPage(1)
              }}
              placeholder="Search drivers…"
              className="max-w-sm"
            />
          }
          empty={!isLoading && items.length === 0}
          emptyState={
            isError
              ? error instanceof Error
                ? error.message
                : 'Failed to load drivers.'
              : 'No drivers match your filters.'
          }
          pagination={
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onChange={setPage}
            />
          }
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Driver</TableHead>
                  <TableHead>License #</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Carrier</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row: any) => {
                  const id = String(row.id ?? '')
                  const name =
                    row.fullName ??
                    row.name ??
                    ([row.firstName, row.lastName].filter(Boolean).join(' ') || '—')
                  const license =
                    row.licenseNumber ?? row.license ?? row.licenseNo ?? '—'
                  const status = String(row.status ?? 'active')
                  const carrier =
                    row.carrier?.name ??
                    row.carrierName ??
                    row.carrier ??
                    '—'
                  return (
                    <TableRow key={id || name}>
                      <TableCell className="font-medium text-foreground">
                        {name}
                      </TableCell>
                      <TableCell className="text-sm font-mono">
                        {license}
                      </TableCell>
                      <TableCell>
                        <StatusChip status={status} />
                      </TableCell>
                      <TableCell className="text-sm">{carrier}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </EntityListShell>
      </div>
    </div>
  )
}
